import { Formik } from 'formik';
import React, { useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import * as yup from 'yup';

import { TextField } from '@components/FormFields';
import TIModal from '@components/TIModal';
import { useProfileUpdate } from '@hooks';
import { CURRENT_USER_KEY } from '@utils/backend-api/currentUser';

import { nameSchema } from '../SettingsPage/Account/schema';

interface PromptForNameModalProps {
  userId: number;
  isOpen: boolean;
  onClose: () => void;
}

const PromptForNameModal = ({ userId, isOpen, onClose }: PromptForNameModalProps) => {
  const queryClient = useQueryClient();
  const { saveProfile, isSavingProfile, showSave, saveStatus, error } = useProfileUpdate(userId);

  useEffect(() => {
    if (saveStatus === 'Saved') onClose();
    if (error) toast.error('There was an error updating your name, please try again.');
  }, [onClose, saveStatus, error]);

  const handleSubmit = async (values: { name: string }) => {
    saveProfile({ name: values.name, profile_enabled: true });
    // Mark current user as stale since we changed the name
    queryClient.invalidateQueries(CURRENT_USER_KEY);
  };

  const buttonText = isSavingProfile ? 'Saving' : showSave ? 'Saved' : 'Enable Profile';

  return (
    <TIModal isOpen={isOpen} onClose={onClose} id="PromptForNameModal">
      <Formik
        initialValues={{ name: '' }}
        onSubmit={handleSubmit}
        validationSchema={yup.object().shape({
          name: nameSchema,
        })}
      >
        {(formik) => (
          <Form noValidate onSubmit={formik.handleSubmit}>
            <div className="text-center">
              Your full name is required before you can enable your profile.
            </div>
            <TextField name="name" label="Full Name" />
            <Button
              className="profile-button --red --full-width"
              type="submit"
              disabled={!(formik.isValid && formik.dirty) || isSavingProfile}
            >
              {buttonText}
            </Button>
          </Form>
        )}
      </Formik>
    </TIModal>
  );
};

export default PromptForNameModal;
